import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Plus, Presentation, Sparkles } from "lucide-react";
import { focusApi } from "../../services/focusApi";

export const formatDate = (iso) => {
  if (!iso) return "";
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? ""
    : date.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
};

export const StatusPill = ({ focus }) =>
  focus.status === "presented" ? (
    <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
      Présenté le {formatDate(focus.presented_at)}
    </span>
  ) : (
    <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-700">À présenter</span>
  );

const FocusCard = ({ focus, onOpen, showAuthor }) => (
  <button
    type="button"
    onClick={onOpen}
    className="flex flex-col gap-2 rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:border-indigo-200 hover:shadow"
  >
    <div className="flex items-start justify-between gap-2">
      <h3 className="text-base font-semibold text-slate-900">{focus.title}</h3>
      <StatusPill focus={focus} />
    </div>
    {focus.summary && <p className="line-clamp-2 text-sm text-slate-500">{focus.summary}</p>}
    <p className="text-xs text-slate-400">
      {showAuthor ? `${focus.author_name} · ` : ""}
      {focus.slide_count} diapo{focus.slide_count > 1 ? "s" : ""}
      {focus.circle_name ? ` · ${focus.circle_name}` : " · tous mes cercles"}
    </p>
  </button>
);

const FocusTechPage = () => {
  const navigate = useNavigate();
  const [mine, setMine] = useState(null);
  const [team, setTeam] = useState([]);
  const [circles, setCircles] = useState([]);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState({ title: "", summary: "", circle_id: "" });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([focusApi.list("mine"), focusApi.list("all"), focusApi.circles()])
      .then(([own, all, circleList]) => {
        if (cancelled) return;
        setMine(own);
        setTeam(all.filter((focus) => !focus.mine));
        setCircles(circleList);
      })
      .catch((err) => !cancelled && setError(err.message));
    return () => {
      cancelled = true;
    };
  }, []);

  const create = async (event) => {
    event.preventDefault();
    if (!draft.title.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const focus = await focusApi.create({
        title: draft.title.trim(),
        summary: draft.summary.trim(),
        circle_id: draft.circle_id ? Number(draft.circle_id) : null,
      });
      navigate(`/tools/focus-tech/${focus.id}`);
    } catch (err) {
      setError(err.message);
      setCreating(false);
    }
  };

  const myCircles = circles.filter((circle) => circle.member);
  return (
    <div className="mx-auto max-w-6xl space-y-6 py-6">
      <div>
        <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
          <Presentation className="h-3.5 w-3.5" />
          Réunions tactiques
        </div>
        <h1 className="mt-3 text-3xl font-semibold tracking-tight text-slate-950">Focus tech</h1>
        <p className="mt-1 max-w-3xl text-sm text-slate-500">
          Une petite présentation à toute l'équipe, juste avant l'ordre du jour de la tactique. Prépare tes diapos ici
          (HTML libre : schémas, animations, jumeaux numériques en 3D) ; le jour J, le secrétaire lance ton Focus et
          c'est toi qui le fais défiler, sur l'écran de chacun.
        </p>
      </div>

      {error && <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <div className="grid gap-4 lg:grid-cols-[1fr_22rem]">
        <section className="space-y-3">
          <h2 className="text-sm font-bold uppercase tracking-wider text-slate-400">Mes Focus</h2>
          {mine === null ? (
            <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
          ) : mine.length === 0 ? (
            <p className="text-sm italic text-slate-400">Aucun Focus pour l'instant : crée le premier à droite.</p>
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {mine.map((focus) => (
                <FocusCard key={focus.id} focus={focus} onOpen={() => navigate(`/tools/focus-tech/${focus.id}`)} />
              ))}
            </div>
          )}
        </section>

        <aside className="space-y-4">
          <form onSubmit={create} className="space-y-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
              <Plus size={15} /> Nouveau Focus tech
            </h2>
            <input
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              maxLength={200}
              placeholder="Titre (ex. Jumeau numérique de la grappe CHF-2)"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-100"
            />
            <textarea
              value={draft.summary}
              onChange={(e) => setDraft({ ...draft, summary: e.target.value })}
              maxLength={2000}
              rows={2}
              placeholder="Le sujet en une phrase (facultatif)"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-100"
            />
            <label className="block text-xs text-slate-500">
              Pour la tactique du cercle
              <select
                value={draft.circle_id}
                onChange={(e) => setDraft({ ...draft, circle_id: e.target.value })}
                className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-2 text-sm"
              >
                <option value="">N'importe lequel de mes cercles</option>
                {(myCircles.length ? myCircles : circles).map((circle) => (
                  <option key={circle.id} value={circle.id}>{circle.name}</option>
                ))}
              </select>
            </label>
            <button
              type="submit"
              disabled={!draft.title.trim() || creating}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Créer
            </button>
          </form>

          <div className="space-y-2 rounded-xl border border-indigo-100 bg-indigo-50/60 p-4 text-sm text-slate-600">
            <h2 className="flex items-center gap-2 font-semibold text-indigo-900">
              <Sparkles size={15} /> Avec une IA
            </h2>
            <p>
              Claude (ou tout agent branché sur le MCP Deepflow) crée et corrige tes diapos avec les outils
              <code className="mx-1 rounded bg-white px-1 text-xs">focus__*</code> et regarde le rendu avant de te
              répondre. Par exemple : « Prépare mon Focus tech sur les réseaux de Bragg, 5 diapos, avec un schéma 3D
              animé de la grappe ».
            </p>
            <p>
              Un jumeau numérique déjà prêt (page HTML autonome) : « Ajoute C:\…\jumeau.html à mon Focus », ou bouton
              « Importer une page HTML » dans le Focus. Ses gestes (rotation, boutons, curseurs) seront suivis par tous
              pendant la présentation.
            </p>
          </div>
        </aside>
      </div>

      {team.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-sm font-bold uppercase tracking-wider text-slate-400">Ceux de l'équipe</h2>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {team.map((focus) => (
              <FocusCard key={focus.id} focus={focus} showAuthor
                         onOpen={() => navigate(`/tools/focus-tech/${focus.id}`)} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default FocusTechPage;
